import { useState } from 'react';
import { format } from 'date-fns';
import type { Message } from '../../lib/api';
import { actionsApi } from '../../lib/api';
import { Lock, Image, FileText, Download, Check, CheckCheck, X, Loader2, Zap } from '../../lib/icons';
import RoleChip from '../ui/RoleChip';

interface Props {
  message: Message;
  currentUserId: string;
  threadType?: string;
  notesMode?: boolean;
  /** Outbound message effectively carries the agent's name (thread.sender_identity resolved against the workspace default). */
  signed?: boolean;
}

export default function MessageBubble({ message, currentUserId, threadType, notesMode, signed }: Props) {
  const isOwn = message.sender_user_id === currentUserId;
  const isInternal = message.is_internal;
  const isSystem = message.is_system;
  const time = format(new Date(message.created_at), 'HH:mm');

  // Dim the external conversation for context while writing an internal note,
  // so the client's last message stays visible without competing for focus.
  // Message mode never dims anything — only note mode tints the non-focused side.
  let modeOpacity = 1;
  if (threadType === 'client' && !isSystem && notesMode === true) {
    modeOpacity = isInternal ? 1 : 0.6;
  }

  // ── System message ─────────────────────────────────────────────────────────
  if (isSystem) {
    return (
      <div style={{
        textAlign: 'center', color: 'var(--xs)', fontSize: '0.72rem',
        padding: '4px 12px', fontStyle: 'italic',
      }}>
        {message.content}
      </div>
    );
  }

  // ── @agent action card — confirmation gate, rendered inline (PRD §7.2) ─────
  if (message.message_type === 'action' && message.pending_action) {
    return <ActionCard message={message} />;
  }

  // ── Internal note (only styled as such in client threads) ─────────────────
  if (isInternal && threadType === 'client') {
    return (
      <div style={{
        opacity: modeOpacity, transition: 'opacity .2s',
        background: 'var(--note-surface)',
        border: '1px solid var(--note-border)',
        borderLeft: '3px solid var(--note-accent)',
        borderRadius: '3px 10px 10px 3px',
        padding: '8px 14px',
        display: 'flex', flexDirection: 'column', gap: 4,
        maxWidth: '80%',
        alignSelf: isOwn ? 'flex-end' : 'flex-start',
      }}>
        <div style={{
          fontSize: '0.67rem', fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '0.07em', color: 'var(--note-label)',
          display: 'flex', alignItems: 'center', gap: 5,
        }}>
          <Lock size={11} strokeWidth={1.8} /> INTERNAL NOTE
          {message.sender_name && (
            <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: 'var(--note-time)' }}>
              · {message.sender_name}
            </span>
          )}
        </div>
        <div style={{ fontSize: '0.8rem', color: 'var(--note-text)', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {message.content}
        </div>
        <div style={{ fontSize: '0.65rem', color: 'var(--note-time)', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <span>Not delivered to the client</span>
          <span>{time}</span>
        </div>
      </div>
    );
  }

  // ── Regular message ────────────────────────────────────────────────────────
  const senderName = message.sender_name ?? message.sender_contact_name ?? 'Unknown';
  const isClientMessage = !!message.sender_contact_id;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', maxWidth: '66%', gap: 2,
      alignSelf: isOwn ? 'flex-end' : 'flex-start',
      opacity: modeOpacity, transition: 'opacity .2s',
    }}>
      {/* Sender name (for non-own messages) */}
      {!isOwn && (
        <div style={{
          fontSize: '0.67rem', color: 'var(--xs)', marginBottom: 2, paddingLeft: 2,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          {senderName}
          {message.sender_role && <RoleChip role={message.sender_role} />}
        </div>
      )}

      {/* Bubble */}
      <div style={{
        padding: '8px 12px', fontSize: '0.81rem', lineHeight: 1.5,
        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        ...(isOwn
          ? {
              background: isClientMessage ? 'var(--surface-bubble-client)' : 'var(--surface-bubble-out)',
              color: isClientMessage ? 'var(--text-primary)' : 'var(--text-on-accent)', fontWeight: 500,
              borderRadius: '10px 10px 3px 10px',
              boxShadow: '0 2px 8px var(--accent-tint)',
            }
          : {
              background: 'var(--surface-bubble-in)', color: 'var(--text-primary)',
              border: '1px solid var(--border-subtle)', borderRadius: '10px 10px 10px 3px',
            }
        ),
      }}>
        {/* Media attachment — download only, no in-app preview */}
        {message.media_url && (
          <div style={{ marginBottom: message.content ? 6 : 0 }}>
            {message.message_type === 'image' ? (
              <a
                href={message.media_url}
                download={message.media_filename ?? true}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '5px 10px', borderRadius: 3,
                  background: isOwn ? 'rgba(255,255,255,.15)' : 'rgba(0,0,0,.06)',
                  color: 'inherit', textDecoration: 'none', fontSize: '0.78rem',
                }}
              >
                <Image size={13} strokeWidth={1.8} />
                <span style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {message.media_filename ?? 'image'}
                </span>
                <Download size={11} strokeWidth={1.8} style={{ opacity: 0.7 }} />
              </a>
            ) : (
              <a
                href={message.media_url}
                download={message.media_filename ?? true}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '5px 10px', borderRadius: 3,
                  background: isOwn ? 'rgba(255,255,255,.15)' : 'rgba(0,0,0,.06)',
                  color: 'inherit', textDecoration: 'none', fontSize: '0.78rem',
                }}
              >
                <FileText size={13} strokeWidth={1.8} />
                <span style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {message.media_filename ?? 'file'}
                </span>
                <Download size={11} strokeWidth={1.8} style={{ opacity: 0.7 }} />
              </a>
            )}
          </div>
        )}
        {/* Caption / text body — hide if it's just the raw stored filename with no user text */}
        {message.content && (message.media_url ? message.content !== (message.media_filename ?? '') : true) && (
          <span>{message.content}</span>
        )}
      </div>

      {/* Timestamp + signed-as + delivery status */}
      <div style={{
        fontSize: '0.66rem', color: 'var(--xs)', padding: '0 2px',
        textAlign: isOwn ? 'right' : 'left',
        display: 'flex', alignItems: 'center', gap: 4, justifyContent: isOwn ? 'flex-end' : 'flex-start',
      }}>
        {isOwn && signed && message.sender_name && (
          <span>Signed as {message.sender_name} ·</span>
        )}
        {time}
        {isOwn && message.wa_status && (
          <span style={{ color: message.wa_status === 'read' ? 'var(--blue)' : 'var(--xs)', display: 'flex', alignItems: 'center' }}>
            {message.wa_status === 'read' || message.wa_status === 'delivered'
              ? <CheckCheck size={12} strokeWidth={2} />
              : <Check size={12} strokeWidth={2} />}
          </span>
        )}
      </div>
    </div>
  );
}

// ── @agent confirmation card ──────────────────────────────────────────────────
// A message, not a modal (PRD §7.2). The card's own state (pending → confirmed
// → executing → succeeded/failed/cancelled/expired) arrives live via the
// action:updated socket event — the underlying thread_messages row is never
// edited, only this rendering reacts to the patched pending_action.
function ActionCard({ message }: { message: Message }) {
  const pa = message.pending_action!;
  const [busy, setBusy] = useState<'confirm' | 'cancel' | null>(null);
  const [understood, setUnderstood] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const actionLabel = pa.actionType.replace(new RegExp(`^${pa.integrationKey}_`), '').replace(/_/g, ' ');
  const integrationLabel = pa.integrationKey.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const isPending = pa.status === 'pending_confirmation';
  const needsExtraStep = pa.injectionFlagged && !understood;

  async function handle(action: 'confirm' | 'cancel') {
    setBusy(action);
    setLocalError(null);
    try {
      await (action === 'confirm' ? actionsApi.confirm(pa.id) : actionsApi.cancel(pa.id));
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setLocalError(msg ?? `Failed to ${action} — try again.`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div style={{
      alignSelf: 'flex-start', maxWidth: '82%',
      background: 'var(--note-surface)',
      border: `1px solid ${pa.injectionFlagged ? 'var(--red)' : 'var(--note-border)'}`,
      borderLeft: `3px solid ${pa.injectionFlagged ? 'var(--red)' : 'var(--note-accent)'}`,
      borderRadius: '3px 10px 10px 3px',
      padding: '10px 14px',
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.67rem', fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--note-label)',
      }}>
        <Zap size={12} strokeWidth={1.8} /> AGENT
        <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: 'var(--note-time)' }}>
          · {statusText(pa.status)}
        </span>
      </div>

      <div style={{ fontSize: '0.82rem', color: 'var(--note-text)', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {message.content}
      </div>

      {isPending && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {needsExtraStep ? (
            <button onClick={() => setUnderstood(true)} style={cardBtnStyle('warn')}>Yes, I understand</button>
          ) : (
            <button onClick={() => handle('confirm')} disabled={busy !== null} style={cardBtnStyle('go')}>
              {busy === 'confirm' ? <Loader2 size={13} strokeWidth={2} style={{ animation: 'rt-spin .8s linear infinite' }} /> : <Check size={13} strokeWidth={2} />}
              Confirm
            </button>
          )}
          <button onClick={() => handle('cancel')} disabled={busy !== null} style={cardBtnStyle('cancel')}>
            {busy === 'cancel' ? <Loader2 size={13} strokeWidth={2} style={{ animation: 'rt-spin .8s linear infinite' }} /> : <X size={13} strokeWidth={2} />}
            Cancel
          </button>
        </div>
      )}

      {localError && <div style={{ fontSize: '0.72rem', color: 'var(--red)' }}>{localError}</div>}

      <div style={{ fontSize: '0.65rem', color: 'var(--note-time)' }}>
        {integrationLabel} · {actionLabel}
      </div>
    </div>
  );
}

function statusText(status: NonNullable<Message['pending_action']>['status']): string {
  switch (status) {
    case 'pending_confirmation': return 'waiting for confirmation';
    case 'confirmed': return 'confirmed — starting…';
    case 'executing': return 'running…';
    case 'succeeded': return 'done';
    case 'failed': return 'failed';
    case 'cancelled': return 'cancelled';
    case 'expired': return 'expired';
  }
}

function cardBtnStyle(kind: 'go' | 'cancel' | 'warn'): React.CSSProperties {
  const base: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.76rem', fontWeight: 700,
    padding: '5px 12px', borderRadius: 5, cursor: 'pointer', border: '1px solid transparent',
  };
  if (kind === 'go') return { ...base, background: 'var(--v1)', color: 'var(--text-on-accent)' };
  if (kind === 'warn') return { ...base, background: 'var(--amber)', color: '#1A1200' };
  return { ...base, background: 'transparent', borderColor: 'var(--border-subtle)', color: 'var(--text-primary)' };
}
